import { AxiosResponseHeaders, AxiosHeaderValue } from 'axios';

import { SubSystem, Tag } from './folder';

type CustomHeader = AxiosResponseHeaders | Partial<AxiosHeaderValue>;

export enum OP_MODES_DISPLAY_LEVEL {
  DISPLAY_ALL = -1,
}

export enum ModelType {
  UNIVARIATE_ENSEMBLE = 'univariate_ensemble',
  ROLLING_AVERAGE_ENSEMBLE = 'rolling_average_ensemble',
}

export enum DeployedModelType {
  ROLLING_AVERAGE_ENSEMBLE = 'rolling_average_ensemble',
  REGRESSION = 'Regression',
  UNIVARIATE_ENSEMBLE = 'univariate_ensemble',
  DECONSTRUCTED_MODEL = 'deconstructed_model',
}

export enum ModelTypeDisplay {
  UNIVARIATE_ENSEMBLE = 'Univariate',
  ROLLING_AVERAGE_ENSEMBLE = 'Rolling Average',
  DECONSTRUCTED_MODEL = 'Multivariate Model',
}

export enum PreviewPeriodType {
  TRAINING_PERIOD = 'training_period',
  LAST_YEAR = 'last_year',
  LAST_6_MONTHS = 'last_6_months',
  LAST_30_DAYS = 'last_30_days',
  LAST_7_DAYS = 'last_7_days',
  LAST_DAY = 'last_day',
  CUSTOM = 'custom',
}

export interface ModelFormValues {
  model_id?: number | null;
  ensemble_id?: number | null;
  ensemble_family?: string | null;
  model_ref?: Model | null;
  system: SubSystem;
  tag: Tag;
  independent_tags: string[];
  model_name: string;
  last_trained: string;
  deployed: string;
  training_period: {
    start_time: string;
    end_time: string;
  };
  preview_period: {
    start_time: string;
    end_time: string;
  };
  preview_period_preset:
    | 'training_period'
    | 'last_year'
    | 'last_6_months'
    | 'last_30_days'
    | 'last_7_days'
    | 'last_day'
    | 'custom';
  model_type: ModelType;
  window_size_display: number;
  window_size_unit: TimeUnit;
  mad?: number;
  mean?: number;
  algorithm: ModelAlgorithmMetrics[];
  anomaly_criteria: ModelAnomalyCriteria;
  rmse_threshold: number;
  scheduled_deployment_date: string;
  train_type: TrainOptions;
  deploy_type: DeployOptions | null;
  op_mode: string;
  exclusions: {
    start_time: string;
    end_time: string;
  }[];
  op_mode_exclusions: {
    start_time: string;
    end_time: string;
  }[];
  status: ModelStatus;
  derived_tag_name?: string | null;
}

export interface ModelAnomalyCriteria {
  mae_multiplier?: {
    upper_bound: number;
    lower_bound: number;
  };
  bias?: {
    upper_bound: number;
    lower_bound: number;
  };
  mad_multiplier?: {
    upper_bound: number;
    lower_bound: number;
  };
  calculated_mean_value?: number;
  calculated_mad?: number;
  calculated_limits?: {
    upper_limit: number;
    lower_limit: number;
  };
}

export enum ModelStatus {
  NEW = 'NEW', // Maintained for create model but not saved yet (Fresh state)
  PLACEHOLDER = 'PLACEHOLDER',
  UPDATING_SETTINGS = 'UPDATING_SETTINGS',
  TRAINING_IN_PROGRESS = 'TRAINING_IN_PROGRESS',
  TRAINING_COMPLETE = 'TRAINING_COMPLETE',
  TRAINING_FAILED = 'TRAINING_FAILED',
  DEPLOYMENT_IN_PROGRESS = 'DEPLOYMENT_IN_PROGRESS',
  DEPLOYMENT_COMPLETE = 'DEPLOYMENT_COMPLETE',
  DEPLOYMENT_FAILED = 'DEPLOYMENT_FAILED',
  DEACTIVATED = 'DEACTIVATED',
}

export enum ModelStatusDisplay {
  NEW = 'New',
  PLACEHOLDER = 'Untrained',
  UPDATING_SETTINGS = 'Updating Settings',
  TRAINING_IN_PROGRESS = 'Training in Progress',
  TRAINING_COMPLETE = 'Training Complete',
  TRAINING_FAILED = 'Training Failed',
  DEPLOYMENT_IN_PROGRESS = 'Deployment in Progress',
  DEPLOYMENT_COMPLETE = 'Deployment Complete',
  DEPLOYMENT_FAILED = 'Deployment Failed',
  DEACTIVATED = 'Deactivated',
}

export enum ModelAlgorithm {
  NeuralNetwork = 'NeuralNetwork',
  LinearRegression = 'LinearRegression',
  LogisticRegression = 'LogisticRegression',
  Tree = 'Tree',
}

export interface ModelAlgorithmMetrics {
  name: ModelAlgorithm;
  anomaly_duration?: string;
  r_value?: number;
  rmse?: number;
  mae?: number;
  is_best?: boolean;
}

export type TimeUnit = 'minutes' | 'hours' | 'days';

export interface Model {
  name: string;
  type: ModelType;
  model_version: number;
  window_size_minutes: number;
  mad?: number;
  mean?: number;
  dependent_tags: {
    name: string;
    system: number;
  }[];
  independent_tags: string[];
  context_tags: string[];
  training_period: {
    start_time: string;
    end_time: string;
  };
  anomaly_criteria: ModelAnomalyCriteria;
  op_mode: string;
  flags: string[];
  algorithm: ModelAlgorithmMetrics[];
  rmse_threshold: number;
  exclusions: {
    start_time: string;
    end_time: string;
  }[];
  training_flow_start_time: string;
  training_flow_end_time: string;
  performance: string;
  deployed_by: string;
  deployment_date: string;
  process_id: number;
  model_id: number;
  status: ModelStatus;
  last_comment: string;
  last_trained: string;
  ensemble_id: number;
  ensemble_family?: string | null;
  derived_tag_name?: string | null;
}

export enum TrainOptions {
  TRAIN_AND_DEPLOY_IF_IMPROVED = 'TRAIN_AND_DEPLOY_IF_IMPROVED',
  TRAIN_AND_DEPLOY = 'TRAIN_AND_DEPLOY',
  TRAIN = 'TRAIN',
  DEPLOY_ONLY = 'DEPLOY_ONLY',
}

export enum DeployOptions {
  SCHEDULE = 'SCHEDULE',
  DEPLOY = 'DEPLOY',
}

export interface TrainAndDeployRequest {
  train_option?: TrainOptions;
  deployment_option?: DeployOptions | null;
  schedule_time?: string;
  ensemble_family?: string | null;
}

export interface CreateModelRequest {
  name: string;
  type: ModelType;
  algorithm?: ModelAlgorithmMetrics[];
  dependent_tags: string[];
  independent_tags?: string[];
  context_tags?: string[];
  training_period: {
    start_time: string;
    end_time: string;
  };
  anomaly_criteria?: ModelAnomalyCriteria;
  rmse_threshold?: number;
  process_id?: number;
  op_mode?: string;
  flags?: string[];
  exclusions: {
    start_time: string;
    end_time: string;
  }[];
  window_size_minutes?: number;
  ensemble_family?: string | null;
}

export interface UpdateModelRequest {
  ensemble_id: number;
  model_id: number;
  name: string;
  type: ModelType;
  algorithm?: ModelAlgorithmMetrics[];
  dependent_tags: string[];
  independent_tags?: string[];
  context_tags?: string[];
  training_period: {
    start_time: string;
    end_time: string;
  };
  anomaly_criteria?: ModelAnomalyCriteria;
  rmse_threshold?: number;
  process_id: number;
  op_mode?: string;
  flags?: string[];
  exclusions: {
    start_time: string;
    end_time: string;
  }[];
  window_size_minutes?: number;
  ensemble_family?: string | null;
}

export interface CreateModelResponse {
  id: number;
  status: string;
  model_version: number;
  ensemble_id: number;
}

export interface OpMode {
  op_mode_tag: string;
  opmode_evaluated_to_true: boolean;
  state_names:
    | {
        state_name: string;
        state_tag_name: string;
        threshold_type: string;
        threshold_value: string;
      }[]
    | null;
}

export enum AssessMode {
  SYSTEM = 'SYSTEM',
  MODEL = 'MODEL',
}

export interface DeployedModelsPayload {
  effectivity_start: string;
  effectivity_end: string;
  folders: number[];
  tags: { tag_name: string; process_id: number }[];
  ensemble_family_id: string;
  ensemble_type?: 'UNIVARIATE' | 'MULTIVARIATE';
}

export class DeployedModelsResult {
  result: Model[];
  request: Record<string, string | number>;
  headers: CustomHeader;
  constructor(data: {
    result?: Model[];
    request?: Record<string, string | number>;
    headers?: CustomHeader;
  }) {
    this.result = data.result || [];
    this.request = data.request || {};
    this.headers = data.headers || {};
  }
}
